// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * @file toConversationId.js
 * @description Helper único para normalizar identificador de conversación. Tipo único: Number.
 */

/**
 * Convierte cualquier valor a ID de conversación (entero). Prohibido usar string como clave.
 * @param {string|number|null|undefined} id - Valor crudo (query, API, WS, router)
 * @returns {number|null} - Entero o null si inválido
 */
export function toConversationId(id) {
  const raw = id;
  if (id == null) {
    if (typeof console !== 'undefined') {
      console.log('[AUDIT][CID_INPUT]', raw);
      console.log('[AUDIT][CID_OUTPUT]', null);
      console.log('[CID][RESOLVE]', id, null);
    }
    return null;
  }
  const n = Number(id);
  if (!Number.isInteger(n)) {
    console.error('[CID][INVALID]', id);
    if (typeof console !== 'undefined') {
      console.log('[AUDIT][CID_INPUT]', raw);
      console.log('[AUDIT][CID_OUTPUT]', null);
      console.log('[CID][RESOLVE]', id, null);
    }
    return null;
  }
  const cid = n;
  if (typeof console !== 'undefined') {
    console.log('[AUDIT][CID_INPUT]', raw);
    console.log('[AUDIT][CID_OUTPUT]', cid);
    console.log('[CID][RESOLVE]', id, n);
  }
  return cid;
}

/**
 * ID canónico de fila `conversaciones` desde UI/API/WS.
 *
 * Reglas:
 * - Priorizar `conversacion_existente_id` y aliases explícitos de conversación antes que `conversacion_id`
 *   (evita filas de contacto donde `conversacion_id` quedó obsoleto o ambiguo).
 * - Descartar cualquier candidato explícito que sea igual al peer/contacto (datos corruptos: conversacion_id = user id).
 * - No usar `contacto_id` / `id_contacto` / peer como conversación.
 * - `record.id` solo como último recurso y nunca si coincide con el ID de contacto/peer del mismo objeto
 *   (evita 10067 usuario vs 10069 conversación real tras createOrGetConversation).
 *
 * @param {Record<string, unknown>|null|undefined} record
 * @returns {number|null}
 */
export function resolveConversationIdFromRecord(record) {
  if (record == null || typeof record !== 'object') return null;

  const peerOrContact = toConversationId(
    record.contacto_id ??
      record.id_contacto ??
      record.usuario_contacto_id ??
      record.otro_usuario_id ??
      null
  );

  const explicitChain = [
    record.conversacion_existente_id,
    record.conversation_id,
    record.conversationId,
    record.conversacion_id,
  ];
  for (const c of explicitChain) {
    const n = toConversationId(c);
    if (n == null) continue;
    if (peerOrContact != null && n === peerOrContact) continue;
    return n;
  }

  const idCand = toConversationId(record.id);
  if (idCand != null && peerOrContact != null && idCand === peerOrContact) {
    return null;
  }
  if (idCand != null) {
    return idCand;
  }

  return null;
}

/** Alias explícito para Asuntos + Mensajes (misma función). */
export const resolveCanonicalConversationIdFromRecord = resolveConversationIdFromRecord;
